import { type SyntheticEvent, useCallback } from "react";
import Debug from "../../../../common-deprecated/Debug";
import { postalPublish } from "../../../../common-deprecated/utils/postalUtils";
import { startCheckoutFlow } from "../../../shared-logic/utils/checkoutUtils";
import { getAemFormsUrl, handleCustomCta, handleUrl } from "../../../shared-logic/utils/ctaButtonUtils";
import { useCarConfigSelector } from "../redux/store";
import type { CtaButtonType } from "../utils/types/AceConfigType";
import {
    POSTAL_CHANNEL_T1_CARCONFIG,
    POSTAL_CHANNEL_T1_DEALER,
    POSTAL_TOPIC_CARCONFIG_SAVE_CONFIG,
    POSTAL_TOPIC_DEALERFINDER_OPEN_IN_OVERLAY,
} from "../../../../common-deprecated/constants";
import useAphCheckoutModal from "./useAphCheckoutModal";
import {
    STEP_BUY_OVERVIEW,
    STEP_LEASE_OVERVIEW,
    STEP_ORDER_OVERVIEW,
    STEP_RESERVE_OVERVIEW,
    trackCtaCheckout,
    trackOrderCTA,
    trackTopNavigationClickCta,
} from "../utils/tracking";
import { useAphStepTracking } from "./useAphStepTracking";

export type UseCtaButtonType = {
    onClick: (event: SyntheticEvent) => void;
    title: string;
    isPrimary: boolean;
    ctaDisabled: boolean;
};

const handleCtaUrl = (url: string, target: "_blank" | "overlayer" | "_self"): void => {
    handleUrl(url, target);
};

// This hook returns the needed props of a CTA button configuration for a link tag.
// Separate hook because it needs client side T1 code.
const useCtaButton = (
    ctaConfig: CtaButtonType,
    useOrderCtaTracking: boolean = false,
    href: string,
): UseCtaButtonType => {
    const save = useCarConfigSelector((state) => state.save);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const section = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);

    const checkoutModals = useAphCheckoutModal();
    const trackStep = useAphStepTracking();

    const tyCode = save?.data?.Personal?.Code || "";
    const target = ctaConfig.target || "_self";
    const trackingEvent = ctaConfig.tracking || ctaConfig.title;

    const onClick = useCallback(
        (event: SyntheticEvent): void => {
            event.preventDefault();
            event.stopPropagation();

            /**
             * Tracking CTA Logic:
             * If the triggered CTA is not a checkout CTA, trigger it's tracking
             */
            if (useOrderCtaTracking && !["go-to-lease", "go-to-buy", "go-to-reserve"].includes(ctaConfig.type || "")) {
                trackOrderCTA(trackingEvent);
            }
            /**
             * Otherwise if the CTA is not in the summary/overview & section is present, track top nav click
             */
            if (!useOrderCtaTracking && section) {
                trackTopNavigationClickCta(trackingEvent, section);
            }

            switch (ctaConfig.type) {
                case "go-to-lease": {
                    const { enabled, active, open } = checkoutModals.lease;
                    if (enabled && !active && open) {
                        trackStep(STEP_LEASE_OVERVIEW);
                        trackCtaCheckout("online-retailing-checkout-leasing", STEP_ORDER_OVERVIEW);
                        open();
                    } else {
                        trackCtaCheckout(
                            "online-retailing-checkout",
                            enabled && active ? STEP_LEASE_OVERVIEW : STEP_ORDER_OVERVIEW,
                        );
                        startCheckoutFlow(save, commonSettings);
                    }
                    break;
                }
                case "go-to-buy": {
                    const { enabled, active, open } = checkoutModals.buy;
                    if (enabled && !active && open) {
                        trackStep(STEP_BUY_OVERVIEW);
                        trackCtaCheckout("online-retailing-checkout-buy", STEP_ORDER_OVERVIEW);
                        open();
                    } else {
                        trackCtaCheckout(
                            "online-retailing-checkout",
                            enabled && active ? STEP_BUY_OVERVIEW : STEP_ORDER_OVERVIEW,
                        );
                        startCheckoutFlow(save, commonSettings);
                    }
                    break;
                }
                case "go-to-reserve": {
                    const { enabled, active, open } = checkoutModals.reserve;
                    if (enabled && !active && open) {
                        trackStep(STEP_RESERVE_OVERVIEW);
                        trackCtaCheckout("online-retailing-checkout-reservation", STEP_ORDER_OVERVIEW);
                        open();
                    } else {
                        trackCtaCheckout(
                            "online-retailing-checkout",
                            enabled && active ? STEP_RESERVE_OVERVIEW : STEP_ORDER_OVERVIEW,
                        );
                        startCheckoutFlow(save, commonSettings, true);
                    }
                    break;
                }
                case "sendtodealer": {
                    const contactDealerLink = getAemFormsUrl("dealer", href || "/forms/contact-dealer.html", tyCode);
                    if (contactDealerLink) handleCtaUrl(contactDealerLink, target);
                    break;
                }
                case "dealerfinder": {
                    postalPublish({
                        channel: POSTAL_CHANNEL_T1_DEALER,
                        topic: POSTAL_TOPIC_DEALERFINDER_OPEN_IN_OVERLAY,
                    });
                    break;
                }
                case "save": {
                    postalPublish({
                        channel: POSTAL_CHANNEL_T1_CARCONFIG,
                        topic: POSTAL_TOPIC_CARCONFIG_SAVE_CONFIG,
                        data: { configCode: tyCode },
                    });
                    break;
                }
                case "download": {
                    const printUrl = `/bin/api/dxp/pdf?tyCode=${tyCode}&country=${commonSettings.country}&language=${commonSettings.language}&brand=${commonSettings.brand}`;
                    window.open(printUrl);
                    break;
                }
                case "testdrive": {
                    const testDriveFormLink = getAemFormsUrl("Test drive", href, tyCode);
                    if (testDriveFormLink) {
                        handleCtaUrl(testDriveFormLink, target);
                    } else {
                        Debug.error("No testdrive CTA url defined");
                    }
                    break;
                }
                case "ebrochurepersonalized": {
                    const brochureFormLink = getAemFormsUrl("brochure", href, tyCode);
                    if (brochureFormLink) {
                        handleCtaUrl(brochureFormLink, target);
                    } else {
                        Debug.error("No brochure CTA url defined");
                    }
                    break;
                }
                case "custom": {
                    handleCustomCta(ctaConfig, href, tyCode, target);
                }
            }
        },
        // No exhaustive to limit to only relevant updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            ctaConfig.type,
            ctaConfig.jsfunction,
            useOrderCtaTracking,
            section,
            checkoutModals.lease,
            checkoutModals.buy,
            checkoutModals.reserve,
            href,
            tyCode,
            target,
            trackingEvent,
        ],
    );

    const requiresTyCode = ["sendtodealer", "save", "download", "testdrive", "ebrochurepersonalized", "custom"];

    return {
        title: ctaConfig.title,
        isPrimary: ctaConfig.isPrimary || false,
        onClick,
        ctaDisabled: requiresTyCode.includes(ctaConfig.type || "") && !tyCode,
    };
};

export default useCtaButton;
